import type { QueryClient } from '@tanstack/react-query';
import { postReply } from './api';

// 2026-06-02 (cowork cache-coherence patch) — React Query bridge.
// replyQueue.ts is intentionally framework-free, so the QueryClient is
// injected at app boot via setReplyQueueQueryClient(). drainReplyQueue
// then invalidates ['conversations', slug] after each successful send so
// the Drip pill (and other Worker-side cache effects of handlePwaReply,
// which adds quote_followup_stop) materialize in the list within seconds
// instead of waiting for the 15s staleTime tick on next focus.
//
// No-op safely when the client hasn't been wired (e.g. SSR / tests).
let injectedQueryClient: QueryClient | null = null;
export function setReplyQueueQueryClient(qc: QueryClient): void {
  injectedQueryClient = qc;
}

/**
 * Offline reply queue.
 *
 * When the user hits Send while offline (navigator.onLine === false) we
 * stash the reply in localStorage instead of failing. On the next 'online'
 * event, drain the queue serially — each successful POST removes the entry,
 * each failure stays for retry on the next online cycle. The view shows a
 * "Pending" badge on optimistic messages while their queue entry is alive.
 *
 * Storage shape:
 *   ss_reply_queue_v1 = QueuedReply[]
 *
 * Each entry contains the slug + conversationId + body needed to replay,
 * plus the optimisticId React Query is using locally so the success swap
 * can match it back up.
 */

const STORAGE_KEY = 'ss_reply_queue_v1';

export interface QueuedReply {
  optimisticId: string;
  slug: string;
  conversationId: string;
  body: string;
  queuedAtIso: string;
}

function readQueue(): QueuedReply[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed as QueuedReply[];
    return [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedReply[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    /* private mode — best effort */
  }
}

export function enqueueReply(reply: QueuedReply): void {
  const q = readQueue();
  q.push(reply);
  writeQueue(q);
}

export function getQueuedReplies(): QueuedReply[] {
  return readQueue();
}

/** Is a given optimistic-id still in the queue (i.e. unsent)? */
export function isReplyQueued(optimisticId: string): boolean {
  return readQueue().some((r) => r.optimisticId === optimisticId);
}

function removeFromQueue(optimisticId: string): void {
  const q = readQueue();
  const next = q.filter((r) => r.optimisticId !== optimisticId);
  if (next.length !== q.length) writeQueue(next);
}

/**
 * Drain the queue. Called on 'online' event and on app boot.
 * Returns the count successfully sent.
 */
export async function drainReplyQueue(): Promise<number> {
  const queue = readQueue();
  if (queue.length === 0) return 0;

  let sent = 0;
  const touchedSlugs = new Set<string>();
  for (const entry of queue) {
    try {
      await postReply(entry.conversationId, entry.slug, entry.body);
      removeFromQueue(entry.optimisticId);
      sent += 1;
      touchedSlugs.add(entry.slug);
      // Notify any listeners (the open ConversationDetail view) so they
      // can drop the "Pending" badge on the corresponding optimistic msg.
      window.dispatchEvent(
        new CustomEvent('ss:reply-queue-drained', {
          detail: { optimisticId: entry.optimisticId, slug: entry.slug },
        }),
      );
    } catch {
      // Leave in queue. Will retry on the next online event.
      break;
    }
  }

  // 2026-06-02 (cowork cache-coherence patch) — invalidate the conversations
  // list + per-conversation queries for every slug we drained, so the Drip
  // pill clears and the row reflects the Worker-side post-reply state
  // (handlePwaReply implicitly takes over: adds quote_followup_stop,
  // removes drip-trigger tags). Without this, offline-queued replies leave
  // the row stale until the 15s staleTime tick fires on next focus.
  if (sent > 0 && injectedQueryClient) {
    const qc = injectedQueryClient;
    for (const slug of touchedSlugs) {
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
    }
  }

  return sent;
}

/**
 * Wire up the global online listener once. Idempotent — safe to call from
 * multiple modules (drain is debounced on the queue itself).
 */
let listenerAttached = false;
export function ensureOnlineListener(): void {
  if (listenerAttached) return;
  listenerAttached = true;
  window.addEventListener('online', () => {
    void drainReplyQueue();
  });
  // Also drain on initial boot in case we were offline last session.
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void drainReplyQueue();
  }
}
